import { useRef, useState, useCallback, useEffect } from 'react';

const SILERO_MODEL_URL = '/assets/models/silero.onnx';

export const useOnnx = () => {
    const workerRef = useRef<Worker | null>(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const initWorker = useCallback(async () => {
        try {
            // fetch model bytes
            const [sileroResp] = await Promise.all([fetch(SILERO_MODEL_URL)]);

            const sileroModelBuffer = await sileroResp.arrayBuffer();

            // create the module worker (type: 'module' required for imports in worker)
            workerRef.current = new Worker(new URL('../../../workers/onnxWorker', import.meta.url));

            // listen for init messages
            workerRef.current.onmessage = (event: MessageEvent) => {
                const { type, error } = event.data;
                if (type === 'init-complete') setReady(true);
                else if (type === 'init-error') setError(error);
            };

            // send model buffers (transferable ArrayBuffers)
            workerRef.current.postMessage({ type: 'init', data: [sileroModelBuffer] }, [sileroModelBuffer]);
        } catch (err: any) {
            setError(err?.message ?? String(err));
        }
    }, []);

    const resetSileroStates = useCallback(() => {
        workerRef.current?.postMessage({ type: 'reset-silero' });
    }, []);

    useEffect(() => {
        return () => {
            workerRef.current?.terminate();
            workerRef.current = null;
        };
    }, []);

    return { workerRef, ready, error, initWorker, resetSileroStates };
};
